import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { DownloadIcon, PrinterIcon, Share2Icon, Award } from "lucide-react";

interface DocumentViewerHeaderProps {
  documentName: string;
  onBack?: () => void;
  onDownload?: () => void;
  onPrint?: () => void;
  onShare?: () => void;
  onDownloadCertificate?: () => void;
  showCertificate?: boolean;
}

const DocumentViewerHeader: React.FC<DocumentViewerHeaderProps> = ({
  documentName,
  onBack,
  onDownload,
  onPrint,
  onShare,
  onDownloadCertificate,
  showCertificate = false,
}) => {
  const { t } = useTranslation(['documents', 'common']);

  return (
    <header
      className="border-b border-border bg-card"
      data-pol-id="fq2lkk"
      data-pol-file-name="document-viewer-header"
      data-pol-file-type="component"
    >
      <div
        className="p-4"
        data-pol-id="udf3u2"
        data-pol-file-name="document-viewer-header"
        data-pol-file-type="component"
      >
        <div
          className="flex items-center justify-between"
          data-pol-id="25dknb"
          data-pol-file-name="document-viewer-header"
          data-pol-file-type="component"
        >
          <div
            className="flex items-center gap-4"
            data-pol-id="l3ym49"
            data-pol-file-name="document-viewer-header"
            data-pol-file-type="component"
          >
            {/* Breadcrumb - hidden on mobile */}
            <div
              className="hidden md:block"
              data-pol-id="qloz1u"
              data-pol-file-name="document-viewer-header"
              data-pol-file-type="component"
            >
              <Breadcrumb
                data-pol-id="6lx7xb"
                data-pol-file-name="document-viewer-header"
                data-pol-file-type="component"
              >
                <BreadcrumbList
                  data-pol-id="1xk31n"
                  data-pol-file-name="document-viewer-header"
                  data-pol-file-type="component"
                >
                  <BreadcrumbItem
                    data-pol-id="ibuv23"
                    data-pol-file-name="document-viewer-header"
                    data-pol-file-type="component"
                  >
                    <BreadcrumbLink
                      href="/"
                      data-pol-id="2bbn4c"
                      data-pol-file-name="document-viewer-header"
                      data-pol-file-type="component"
                    >
                      {t('documents:viewer_breadcrumb_documents')}
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator
                    data-pol-id="wd8fjo"
                    data-pol-file-name="document-viewer-header"
                    data-pol-file-type="component"
                  />
                  <BreadcrumbItem
                    data-pol-id="9o1nhk"
                    data-pol-file-name="document-viewer-header"
                    data-pol-file-type="component"
                  >
                    <BreadcrumbLink
                      href="#"
                      data-pol-id="8x42i4"
                      data-pol-file-name="document-viewer-header"
                      data-pol-file-type="component"
                    >
                      {documentName}
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </div>

            {/* Document name for mobile */}
            <div
              className="md:hidden"
              data-pol-id="eevjxd"
              data-pol-file-name="document-viewer-header"
              data-pol-file-type="component"
            >
              <span
                className="text-sm font-medium"
                data-pol-id="qd66b0"
                data-pol-file-name="document-viewer-header"
                data-pol-file-type="component"
              >
                {documentName}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onDownload}
            >
              <DownloadIcon className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">{t('common:actions.download')}</span>
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={onShare}
            >
              <Share2Icon className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">{t('common:actions.share')}</span>
            </Button>

            {showCertificate && onDownloadCertificate && (
              <Button
                variant="outline"
                size="sm"
                onClick={onDownloadCertificate}
                className="text-blue-600 border-blue-200 hover:bg-blue-50"
              >
                <Award className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">{t('documents:certificate_download')}</span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default DocumentViewerHeader;
